/**
 * The single source of truth for the file types Payaca accepts, keyed by
 * extension, so the extension list and the MIME list cannot drift apart.
 *
 * This package is **client-side**: it validates only what a caller *declares*
 * about a file. Byte-level verification lives in server-backend — do not add it
 * here, and do not add a dependency that only works under Node.
 */

export const UPLOAD_CATEGORIES = {
  image: { label: 'images' },
  document: { label: 'documents' },
  video: { label: 'videos' },
} as const;

export type UploadCategory = keyof typeof UPLOAD_CATEGORIES;

/** Every supported upload category — the widest an allow-list can be. */
export const ALL_UPLOAD_CATEGORIES = Object.keys(
  UPLOAD_CATEGORIES
) as UploadCategory[];

export type FileType = {
  /** Lowercase, dot-prefixed. */
  extension: string;
  /** The MIME type this extension is signed and stored as. */
  mimeType: string;
  category: UploadCategory;
  /**
   * Further MIME types a picker may report for this extension. Used only to
   * resolve an incoming MIME type back to an entry; never used when choosing
   * the type to sign an upload with.
   */
  alsoKnownAs?: readonly string[];
};

/**
 * Order matters: the per-category extension lists derived from this table are
 * surfaced to pickers and to users, and read in the order given here.
 */
const FILE_TYPES: readonly FileType[] = [
  // Images
  { extension: '.png', mimeType: 'image/png', category: 'image' },
  { extension: '.jpeg', mimeType: 'image/jpeg', category: 'image' },
  { extension: '.jpg', mimeType: 'image/jpeg', category: 'image' },
  { extension: '.webp', mimeType: 'image/webp', category: 'image' },
  { extension: '.heic', mimeType: 'image/heic', category: 'image' },
  { extension: '.heif', mimeType: 'image/heif', category: 'image' },
  { extension: '.gif', mimeType: 'image/gif', category: 'image' },
  { extension: '.bmp', mimeType: 'image/bmp', category: 'image' },
  {
    extension: '.tiff',
    mimeType: 'image/tiff',
    category: 'image',
    alsoKnownAs: ['image/x-tiff'],
  },
  { extension: '.tif', mimeType: 'image/tiff', category: 'image' },
  // SVG carries script, so it is served as a download rather than rendered —
  // see `contentDispositionFor`.
  { extension: '.svg', mimeType: 'image/svg+xml', category: 'image' },

  // Documents
  { extension: '.doc', mimeType: 'application/msword', category: 'document' },
  {
    extension: '.docx',
    mimeType:
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    category: 'document',
  },
  { extension: '.pdf', mimeType: 'application/pdf', category: 'document' },
  {
    extension: '.xls',
    mimeType: 'application/vnd.ms-excel',
    category: 'document',
  },
  {
    extension: '.xlsx',
    mimeType:
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    category: 'document',
  },
  { extension: '.txt', mimeType: 'text/plain', category: 'document' },
  {
    extension: '.csv',
    mimeType: 'text/csv',
    category: 'document',
    alsoKnownAs: ['application/csv'],
  },
  { extension: '.rtf', mimeType: 'application/rtf', category: 'document' },
  { extension: '.md', mimeType: 'text/markdown', category: 'document' },
  { extension: '.xml', mimeType: 'application/xml', category: 'document' },
  { extension: '.json', mimeType: 'application/json', category: 'document' },

  // Presentations
  {
    extension: '.ppt',
    mimeType: 'application/vnd.ms-powerpoint',
    category: 'document',
  },
  {
    extension: '.pptx',
    mimeType:
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    category: 'document',
  },

  // OpenDocument
  {
    extension: '.odt',
    mimeType: 'application/vnd.oasis.opendocument.text',
    category: 'document',
  },
  {
    extension: '.ods',
    mimeType: 'application/vnd.oasis.opendocument.spreadsheet',
    category: 'document',
  },
  {
    extension: '.odp',
    mimeType: 'application/vnd.oasis.opendocument.presentation',
    category: 'document',
  },

  // Apple iWork. Zip containers like Office documents, but with no signature
  // `file-type` can recognise — they verify as an unidentified zip.
  {
    extension: '.pages',
    mimeType: 'application/vnd.apple.pages',
    category: 'document',
  },
  {
    extension: '.numbers',
    mimeType: 'application/vnd.apple.numbers',
    category: 'document',
  },
  {
    extension: '.key',
    mimeType: 'application/vnd.apple.keynote',
    category: 'document',
  },

  // CAD and drawings — schematics, site plans and elevations
  {
    extension: '.dwg',
    mimeType: 'image/vnd.dwg',
    category: 'document',
    alsoKnownAs: ['application/acad', 'image/x-dwg'],
  },
  {
    extension: '.dxf',
    mimeType: 'image/vnd.dxf',
    category: 'document',
    alsoKnownAs: ['application/dxf'],
  },
  { extension: '.ifc', mimeType: 'application/x-step', category: 'document' },

  // Saved email, routinely forwarded as evidence of a customer instruction
  { extension: '.eml', mimeType: 'message/rfc822', category: 'document' },
  {
    extension: '.msg',
    mimeType: 'application/vnd.ms-outlook',
    category: 'document',
  },
  { extension: '.ics', mimeType: 'text/calendar', category: 'document' },

  // Videos
  { extension: '.mp4', mimeType: 'video/mp4', category: 'video' },
  { extension: '.mov', mimeType: 'video/quicktime', category: 'video' },
  { extension: '.m4v', mimeType: 'video/x-m4v', category: 'video' },
  { extension: '.webm', mimeType: 'video/webm', category: 'video' },
  { extension: '.3gp', mimeType: 'video/3gpp', category: 'video' },
];

/**
 * SVG is the only accepted format a browser will both render inline and execute
 * script from, so it is always downloaded rather than displayed.
 */
export const contentDispositionFor = (
  fileType: FileType
): 'inline' | 'attachment' =>
  fileType.mimeType === 'image/svg+xml' ? 'attachment' : 'inline';

const BY_EXTENSION = new Map<string, FileType>(
  FILE_TYPES.map((fileType) => [fileType.extension, fileType])
);

/**
 * First entry wins, so an extension listed earlier in `FILE_TYPES` is the one a
 * shared MIME type resolves back to (`image/jpeg` -> `.jpeg`, not `.jpg`).
 */
const BY_MIME_TYPE = new Map<string, FileType>();
for (const fileType of FILE_TYPES) {
  for (const mimeType of [fileType.mimeType, ...(fileType.alsoKnownAs ?? [])]) {
    if (!BY_MIME_TYPE.has(mimeType)) BY_MIME_TYPE.set(mimeType, fileType);
  }
}

/** Normalise an extension to a lowercase, dot-prefixed form (`PDF` -> `.pdf`). */
const normaliseExtension = (extension: string): string => {
  const lower = extension.toLowerCase();
  return lower.startsWith('.') ? lower : `.${lower}`;
};

/**
 * A filename may contain a dot without carrying an extension at all — real
 * uploads include names like `9.52kWh of battery storage`. Callers must treat an
 * unrecognised result as "no extension" and fall back to the reported MIME type.
 */
export const extensionFromFileName = (
  fileName: string | undefined
): string | undefined => {
  if (!fileName) return undefined;
  const lastDot = fileName.lastIndexOf('.');
  if (lastDot <= 0 || lastDot === fileName.length - 1) return undefined;
  return normaliseExtension(fileName.slice(lastDot + 1));
};

export const fileTypeForExtension = (
  extension: string | undefined
): FileType | undefined =>
  extension ? BY_EXTENSION.get(normaliseExtension(extension)) : undefined;

export const fileTypeForMimeType = (
  mimeType: string | undefined
): FileType | undefined =>
  mimeType ? BY_MIME_TYPE.get(mimeType.toLowerCase()) : undefined;

export const categoryFromMimeType = (
  mimeType: string
): UploadCategory | undefined => fileTypeForMimeType(mimeType)?.category;

const fileTypesForCategories = (
  categories: readonly UploadCategory[]
): readonly FileType[] =>
  FILE_TYPES.filter((fileType) => categories.includes(fileType.category));

export const mimeTypesForCategories = (
  categories: readonly UploadCategory[]
): string[] => [
  ...new Set(
    fileTypesForCategories(categories).flatMap((fileType) => [
      fileType.mimeType,
      ...(fileType.alsoKnownAs ?? []),
    ])
  ),
];

export const extensionsForCategories = (
  categories: readonly UploadCategory[]
): string[] => [
  ...new Set(fileTypesForCategories(categories).map((f) => f.extension)),
];

/**
 * Everything a file picker needs to describe and filter by a set of categories:
 * the extensions to accept, and a summary to show in place of listing them.
 */
export const acceptedFilesForCategories = (
  categories: readonly UploadCategory[]
): { extensions: string[]; summary: string } => ({
  extensions: extensionsForCategories(categories),
  summary: describeUploadCategories(categories),
});

/**
 * Human-readable summary of the accepted categories, e.g.
 * `['image', 'document', 'video']` -> `"images, documents and videos"`.
 */
export const describeUploadCategories = (
  categories: readonly UploadCategory[]
): string => {
  const labels = [...new Set(categories)].map(
    (c) => UPLOAD_CATEGORIES[c].label
  );
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0];
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
};
